import { useEffect, useRef } from 'react';
import {
  fetchOpenOrdersGroupsSilent,
  fingerprintOpenOrderGroups,
} from '../services/boardNovosChamadosService';

/** Aba em foco: checagem mais frequente. */
export const CUSTOMERS_OPEN_POLL_FOCUS_MS = 25_000;
/** Aba em segundo plano: mais espaçado. */
export const CUSTOMERS_OPEN_POLL_IDLE_MS = 50_000;

/**
 * Poll leve só de Em aberto para sessões Board com Customers.
 * Atualiza badge "Novos chamados" + coluna quando a assinatura OS/status muda.
 * Não toca colunas de técnicos nem spinner de refresh.
 * Usa If-None-Match / 304 quando o backend confirma que nada mudou.
 *
 * @param {{
 *   enabled: boolean,
 *   openOrderGroups: object[]|null,
 *   onOpenOrdersChange: (data: object[], dataSource?: string) => void,
 *   pauseRef?: { current: boolean },
 * }} opts
 */
export function useCustomersOpenOrdersPoll({
  enabled,
  openOrderGroups,
  onOpenOrdersChange,
  pauseRef,
}) {
  const onChangeRef = useRef(onOpenOrdersChange);
  onChangeRef.current = onOpenOrdersChange;

  const signatureRef = useRef('');
  const etagRef = useRef(null);

  useEffect(() => {
    signatureRef.current = fingerprintOpenOrderGroups(openOrderGroups);
  }, [openOrderGroups]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let timerId = null;

    const clearTimer = () => {
      if (timerId != null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };

    const delayMs = () =>
      typeof document !== 'undefined' && document.visibilityState === 'visible'
        ? CUSTOMERS_OPEN_POLL_FOCUS_MS
        : CUSTOMERS_OPEN_POLL_IDLE_MS;

    const schedule = () => {
      clearTimer();
      if (cancelled) return;
      timerId = window.setTimeout(runTick, delayMs());
    };

    const runTick = async () => {
      if (cancelled) return;
      if (pauseRef?.current) {
        schedule();
        return;
      }
      try {
        const result = await fetchOpenOrdersGroupsSilent({ etag: etagRef.current });
        if (cancelled || !result) {
          schedule();
          return;
        }
        if (result.notModified) {
          if (result.etag) etagRef.current = result.etag;
          schedule();
          return;
        }
        if (result.etag) etagRef.current = result.etag;
        const nextSig = fingerprintOpenOrderGroups(result.data);
        if (nextSig !== signatureRef.current) {
          signatureRef.current = nextSig;
          onChangeRef.current?.(result.data, result.dataSource);
        }
      } catch {
        /* silencioso */
      }
      if (!cancelled) schedule();
    };

    schedule();

    const onVisibility = () => {
      if (cancelled) return;
      clearTimer();
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        runTick();
      } else {
        schedule();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, pauseRef]);
}
